import * as Sentry from '@sentry/vue'
import { pwaConfig } from './pwaTools'
import { sentryCountMetric } from './sentryMetrics.ts'

const SW_LIFECYCLE_CATEGORY = 'sw.lifecycle'

function scriptUrlOf(worker: ServiceWorker | null | undefined): string | undefined {
  return worker?.scriptURL
}

function commonData(extra?: Record<string, unknown>): Record<string, unknown> {
  return {
    appVersion: __APP_VERSION__,
    displayMode: pwaConfig.displayMode,
    browserInfoName: pwaConfig.browserInfo?.name,
    browserInfoOs: pwaConfig.browserInfo?.os,
    controllerScriptUrl: scriptUrlOf(
      typeof navigator !== 'undefined' ? navigator.serviceWorker?.controller : null,
    ),
    ...extra,
  }
}

function breadcrumb(message: string, data?: Record<string, unknown>): void {
  Sentry.addBreadcrumb({
    category: SW_LIFECYCLE_CATEGORY,
    message,
    level: 'info',
    data: commonData(data),
  })
}

/** New SW found via registration.updatefound (often a deploy / sw.js byte change). */
export function reportSwUpdateFound(hadPriorActive: boolean): void {
  sentryCountMetric('sw.update_found')
  breadcrumb('Service worker update found', {
    hadPriorActive,
    kind: hadPriorActive ? 'replace' : 'first_or_cold',
  })
}

/** Installing worker reached `installed` (waiting or about to activate). */
export function reportSwInstalled(): void {
  sentryCountMetric('sw.installed')
  breadcrumb('Service worker installed')
}

/** Installing worker reached `activated`. */
export function reportSwActivated(): void {
  sentryCountMetric('sw.activated')
  breadcrumb('Service worker activated')
}

/**
 * This tab's controlling worker changed.
 * `hadPriorController` false ≈ first claim; true ≈ replace while page was open.
 */
export function reportSwControllerChange(hadPriorController: boolean): void {
  sentryCountMetric('sw.controllerchange')
  breadcrumb('Service worker controller changed', {
    hadPriorController,
    kind: hadPriorController ? 'replace' : 'first_claim',
    newControllerScriptUrl: scriptUrlOf(
      typeof navigator !== 'undefined' ? navigator.serviceWorker?.controller : undefined,
    ),
  })
}

/** SW posted `SW_ACTIVATED` after clients.claim (may fire on every open client). */
export function reportSwActivatedMessage(): void {
  sentryCountMetric('sw.activated_message')
  breadcrumb('Service worker SW_ACTIVATED message received')
}
